/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListMcpEndpointsRequest,
  ListMcpEndpointsSecurity,
} from "../models/operations/listmcpendpoints.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildMcpEndpointsQuery,
  McpEndpointsQueryData,
  prefetchMcpEndpoints,
  queryKeyMcpEndpoints,
} from "./mcpEndpoints.core.js";
export {
  buildMcpEndpointsQuery,
  type McpEndpointsQueryData,
  prefetchMcpEndpoints,
  queryKeyMcpEndpoints,
};

export type McpEndpointsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listMcpEndpoints mcpEndpoints
 *
 * @remarks
 * List MCP endpoints for a project. Optionally filter to only those associated with a specific MCP server or meta MCP server (not both).
 */
export function useMcpEndpoints(
  request?: ListMcpEndpointsRequest | undefined,
  security?: ListMcpEndpointsSecurity | undefined,
  options?: QueryHookOptions<McpEndpointsQueryData, McpEndpointsQueryError>,
): UseQueryResult<McpEndpointsQueryData, McpEndpointsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildMcpEndpointsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listMcpEndpoints mcpEndpoints
 *
 * @remarks
 * List MCP endpoints for a project. Optionally filter to only those associated with a specific MCP server or meta MCP server (not both).
 */
export function useMcpEndpointsSuspense(
  request?: ListMcpEndpointsRequest | undefined,
  security?: ListMcpEndpointsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    McpEndpointsQueryData,
    McpEndpointsQueryError
  >,
): UseSuspenseQueryResult<McpEndpointsQueryData, McpEndpointsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildMcpEndpointsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setMcpEndpointsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      mcpServerId?: string | undefined;
      metaMcpServerId?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: McpEndpointsQueryData,
): McpEndpointsQueryData | undefined {
  const key = queryKeyMcpEndpoints(...queryKeyBase);

  return client.setQueryData<McpEndpointsQueryData>(key, data);
}

export function invalidateMcpEndpoints(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      mcpServerId?: string | undefined;
      metaMcpServerId?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "mcpEndpoints", "list", ...queryKeyBase],
  });
}

export function invalidateAllMcpEndpoints(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "mcpEndpoints", "list"],
  });
}
